import { computed, shallowRef, watch } from "vue";
import { useAsyncData } from "./useAsyncData";
import { getOfficesV2 } from '../assets/js/utils';
import { toValue } from "../assets/js/vue/utlis/toValue";
import { EMBProductCode } from "../components-manual/widgets/MeetingForm/const";

/**
 * Present office workdays
 * @param {string} officeId - office bitrix code
 * @param {object[]} officesRaw - offices bitrix data
 */
const officeWorkdays = (officeId, officesRaw) => {
    if (!officeId || !officesRaw) return '';
    const shedule = officesRaw.filter(value => value.id.toString() === officeId)[0]?.attributes.schedule.individuals;
    if (!shedule) return '';
    const sunday = shedule.sun ? `вс ${shedule.sun?.start}-${shedule.sun?.end}` : 'вс - выходной';
    const saturday = shedule.sat ? `сб ${shedule.sat?.start}-${shedule.sat?.end}` : 'сб - выходной';
    return `пн-пт ${shedule.week.start}-${shedule.week.end}, ${saturday}, ${sunday}`;
};

/**
 * @typedef ListState
 * @type {object}
 * @property {import('vue').Ref<object>} valueObject - элемент из списка, соответствующий value
 * @property {import('vue').Ref<string>} workDays - office workdays
 * @property {import('vue').Ref<object[]>} data - массив офисов для селекта
 */

/**
 * Получает опции для селекта (все параметры реактивные)
 * @param {string} filterCityCode - код города ифноблока битрикс
 * @param {EMBProductCode} filterProductCode - код продукта
 * @param {string} valueRaw - значение code для определения valueObject
 * @param {object} options options
 * @param {boolean} options.immediate - опция аналогично useAsyncData
 * @param {boolean} options.disabled - отключить отслеживание параметров и запрос списка
 * @returns {ListState}
 */
export const useBankOfficeList = (filterCityCode, filterProductCode, valueRaw, options = {}) => {
    const immediate = !toValue(options?.disabled) && options?.immediate || false;
    const value = computed(() => toValue(valueRaw));

    const officesQuery = computed(() => {
        const query = {};
        switch (toValue(filterProductCode)) {
            case EMBProductCode.ESCROW:
                query.isEscrow = 'y';
                break;
            case EMBProductCode.PREMIUM:
                query.premium = 'y';
                break;
            case EMBProductCode.DEBIT_CARD:
            case EMBProductCode.SALARY_CARD:
            case EMBProductCode.STICKER:
                query['card.debit'] = 'y';
                break;
            default:
                break;
        }
        return query;
    });

    const sort = computed(() => {
        return toValue(filterCityCode) === '146470' ? 'sort' : 'name'; // питер сортируем по значению сортировки в админке
    });

    const watched = () => toValue(options?.disabled) ? null : [officesQuery.value, toValue(filterCityCode)];

    const { pending, data: officesRaw, execute, error } = useAsyncData(async ({ signal }) => {
        let officesList = [];

        if (toValue(options?.disabled)) return officesList;

        const res = await getOfficesV2(toValue(filterCityCode), officesQuery.value, sort.value, { signal });

        officesList = res.data.data ?? [];

        return officesList;
    }, { deep: false, watch: watched, default: () => [], immediate });

    const data = computed(() => {
        const officesList = officesRaw.value?.map((office) => ({
            const: `${office.id}`,
            title: office.attributes.name.short,
            raw: office,
        })) ?? [];
        return officesList;
    });

    const workDays = computed(() => officeWorkdays(value.value, officesRaw.value));

    /** объект с выбранным значением (по const итема из списка) value */
    const valueObject = shallowRef(null);

    watch([data, value], ([data, value]) => {
        if (!value || data.length === 0) {
            valueObject.value = null;
            return;
        }
        valueObject.value = data.find((item) => item.const === value) ?? null;
    });

    return {
        data,
        pending,
        execute,
        error,
        valueObject,
        workDays,
    };
};
